import { API_BASE } from "./config/api";
import { getRuntimeAppMetadata } from "./config/appVariant";
import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Form,
  Input,
  Modal,
  Select,
  Steps,
  Typography,
  message,
} from "antd";
import {
  CheckCircleOutlined,
  IdcardOutlined,
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
  SafetyOutlined,
  UserOutlined,
  WhatsAppOutlined,
  UnlockOutlined,
  KeyOutlined,
  MobileOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import "./ClientLogin.css";
import mfsetuLogo from "./assets/mfsetu-mywealth-logo.png";
import { clearClientSession, clearDistributorSession } from "./config/auth";
import {
  enableBiometricLogin,
  getBiometricCapability,
  getBiometricAccountHint,
  hasBiometricLogin,
  isBiometricCancellation,
  isNativeBiometricRealm,
  readBiometricLogin,
} from "./config/biometricAuth";
import { captureRequiredClientLoginLocation } from "./config/clientLoginLocation";
import { hasPinCredential, readPinCredential, savePinCredential, getRememberedUserId, saveRememberedUserId, isClientQuickAuthNative } from "./config/clientQuickAuth";

const { Title, Text } = Typography;

function MpinDigits({ value, onChange, disabled = false, ariaLabel = "MPIN", autoFocus = false, onComplete = null, groupId = "mpin" }) {
  const refs = React.useRef([]);
  const digits = String(value || "").padEnd(4, " ").slice(0, 4).split("");

  React.useEffect(() => {
    if (!disabled && autoFocus) {
      const timer = window.setTimeout(() => refs.current[0]?.focus(), 80);
      return () => window.clearTimeout(timer);
    }
  }, [autoFocus, disabled]);

  const commit = (next) => onChange(next.join("").replace(/ /g, ""));
  const setDigit = (index, raw) => {
    const incoming = String(raw || "").replace(/\D/g, "");
    if (!incoming) return;
    const next = String(value || "").padEnd(4, " ").slice(0, 4).split("");
    next[index] = incoming.slice(-1);
    commit(next);
    if (index < 3) refs.current[index + 1]?.focus();
    else if (onComplete) window.setTimeout(onComplete, 0);
  };
  const handleKeyDown = (index, e) => {
    if (e.key !== "Backspace") return;
    e.preventDefault();
    const next = String(value || "").padEnd(4, " ").slice(0, 4).split("");
    if (next[index] && next[index] !== " ") {
      next[index] = " ";
      commit(next);
      return;
    }
    if (index > 0) {
      next[index - 1] = " ";
      commit(next);
      refs.current[index - 1]?.focus();
    } else {
      refs.current[0]?.focus();
    }
  };
  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4);
    if (!pasted) return;
    e.preventDefault();
    onChange(pasted);
    const target = Math.min(pasted.length, 4) - 1;
    window.setTimeout(() => refs.current[target]?.focus(), 0);
    if (pasted.length === 4 && onComplete) window.setTimeout(onComplete, 0);
  };

  return <div className="mf-mpin-digits" aria-label={ariaLabel}>{digits.map((digit, index) => (
    <input id={`${groupId}-${index}`} ref={(el)=>{refs.current[index]=el}} key={index} className="mf-mpin-digit" type="password" inputMode="numeric" maxLength={1}
      value={digit.trim()} disabled={disabled} aria-label={`${ariaLabel} digit ${index + 1}`}
      onFocus={(e)=>e.target.select()} onChange={(e) => setDigit(index, e.target.value)} onKeyDown={(e)=>handleKeyDown(index,e)} onPaste={handlePaste} />
  ))}</div>;
}

function ClientLogin({ embedded = false }) {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("signin");
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [biometricOffer, setBiometricOffer] = useState(null);
  const [showCredentialLogin, setShowCredentialLogin] = useState(true);
  const [activeSessionPrompt, setActiveSessionPrompt] = useState(null);
  const [pinAvailable, setPinAvailable] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [pinValue, setPinValue] = useState("");
  const [pinLoading, setPinLoading] = useState(false);
  const [pinSetupOffer, setPinSetupOffer] = useState(null);
  const [pinSetupValue, setPinSetupValue] = useState("");
  const [pinSetupConfirm, setPinSetupConfirm] = useState("");
  const [pinSetupError, setPinSetupError] = useState("");
  const [loginMode, setLoginMode] = useState("methods");
  const [pinSetupRequested, setPinSetupRequested] = useState(false);
  const [pinResetRequested, setPinResetRequested] = useState(false);
  const [pinSetupIsReset, setPinSetupIsReset] = useState(false);
  const PIN_SETUP_INTENT_KEY = "mfsetu_pin_setup_intent_v1";
const LAST_SECURE_METHOD_KEY = "mfsetu_client_last_secure_login_v1";
  const [otpLoginOpen, setOtpLoginOpen] = useState(false);
  const [otpLoginUserId, setOtpLoginUserId] = useState(getRememberedUserId());
  const [otpLoginMethod, setOtpLoginMethod] = useState("MOBILE");
  const [otpLoginRequestId, setOtpLoginRequestId] = useState(null);
  const [otpLoginValue, setOtpLoginValue] = useState("");
  const [otpLoginDestination, setOtpLoginDestination] = useState("");
  const [otpLoginLoading, setOtpLoginLoading] = useState(false);
  const [otpLoginCooldown, setOtpLoginCooldown] = useState(0);
  const [signupOtpCooldowns, setSignupOtpCooldowns] = useState({ MOBILE: 0, EMAIL: 0, WHATSAPP: 0 });

  const [forgotUserIdOpen, setForgotUserIdOpen] = useState(false);
  const [forgotUserIdPan, setForgotUserIdPan] = useState("");
  const [forgotUserIdMethod, setForgotUserIdMethod] = useState("MOBILE");
  const [forgotUserIdRequestId, setForgotUserIdRequestId] = useState(null);
  const [forgotUserIdOtp, setForgotUserIdOtp] = useState("");
  const [forgotUserIdDestination, setForgotUserIdDestination] = useState("");
  const [forgotUserIdCooldown, setForgotUserIdCooldown] = useState(0);
  const [forgotUserIdLoading, setForgotUserIdLoading] = useState(false);
  const [forgotUserIdError, setForgotUserIdError] = useState("");
  const [recoveredUserIds, setRecoveredUserIds] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      const hasPin = await hasPinCredential();
      if (!alive) return;
      setPinAvailable(hasPin);

      if (!isNativeBiometricRealm("client")) {
        setShowCredentialLogin(true);
        setLoginMode("methods");
        return;
      }

      const capability = await getBiometricCapability("client");
      const enabled = capability.available ? await hasBiometricLogin("client") : false;
      if (!alive) return;
      setBiometricAvailable(capability.available);
      setBiometricEnabled(enabled);

      const lastSecure = localStorage.getItem(LAST_SECURE_METHOD_KEY) || "";
      if (lastSecure === "pin" && hasPin) {
        setShowCredentialLogin(true);
        setLoginMode("methods");
        setPinOpen(true);
        return;
      }

      if (enabled) {
        setShowCredentialLogin(false);
        window.setTimeout(() => handleBiometricLogin(true), 150);
        return;
      }

      setShowCredentialLogin(true);
      setLoginMode("methods");
    })();
    return () => { alive = false; };
  }, []);

  const finishClientLogin = (token, userId = "", clientName = "") => {
    clearDistributorSession();
    clearClientSession();
    sessionStorage.setItem("client_auth_token", token);
    localStorage.removeItem("client_auth_token");
    localStorage.setItem("client_user_id", userId);
    localStorage.setItem("client_name", clientName);
    window.location.assign("/client/dashboard");
  };

  const completeBiometricLogin = async (
    saved,
    forceLogin = false,
    loginLocation = null
  ) => {
    const location =
      loginLocation || (await captureRequiredClientLoginLocation());

    const appMeta = await getRuntimeAppMetadata();
    const response = await fetch(`${API_BASE}/client_biometric_exchange.php`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-KG-Biometric-Authorization": `Bearer ${saved.deviceCredential}`,
      },
      body: JSON.stringify({
        location,
        force_login: forceLogin,
        ...appMeta,
      }),
    });

    const data = await response.json().catch(() => ({}));

    if (
      data.status === "confirmation_required" ||
      data.code === "ACTIVE_SESSION_EXISTS"
    ) {
      setActiveSessionPrompt({
        loginType: "biometric",
        saved,
        location,
        message:
          data.message ||
          "Your account is already logged in on another device. Do you want to log out the previous session and continue here?",
      });
      return;
    }

    if (!response.ok || data.status !== "ok" || !data.token) {
      throw new Error(data.message || "Biometric session exchange failed.");
    }

    localStorage.setItem(LAST_SECURE_METHOD_KEY, "biometric");
    finishClientLogin(
      data.token,
      data.client?.user_id || saved.userId || "",
      data.client?.client_name || saved.displayName || ""
    );
  };

  const handleBiometricLogin = async (autoStart = false) => {
    setLoginError("");
    setBiometricLoading(true);
    try {
      const saved = await readBiometricLogin("client");
      if (!saved?.deviceCredential) {
        setBiometricEnabled(false);
        setShowCredentialLogin(true);
        setLoginError(
          "Biometric login is not configured. Please use PIN, OTP, or User ID and password."
        );
        return;
      }

      await completeBiometricLogin(saved, false);
    } catch (error) {
      if (isBiometricCancellation(error)) {
        setShowCredentialLogin(true);
        setLoginMode("methods");
      } else {
        setShowCredentialLogin(true);
        setLoginError(
          "Biometric login could not be completed. Please use PIN, OTP, or User ID and password."
        );
      }
    } finally {
      setBiometricLoading(false);
    }
  };

  const acceptBiometricOffer = async () => {
    if (!biometricOffer) return;
    setBiometricLoading(true);
    try {
      const response = await fetch(`${API_BASE}/client_biometric_enroll.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-KG-Client-Authorization": `Bearer ${biometricOffer.token}`,
        },
        body: JSON.stringify({ device_label: "MFSetu - MyWealth" }),
      });
      const data = await response.json().catch(() => ({}));
      const deviceCredential = data?.device_credential;
      if (!response.ok || data.status !== "ok" || !deviceCredential) {
        throw new Error(data.message || "Biometric enrollment failed.");
      }

      await enableBiometricLogin("client", {
        deviceCredential,
        userId: biometricOffer.userId,
        displayName: biometricOffer.displayName,
      });
      setBiometricEnabled(true);
      finishClientLogin(biometricOffer.token, biometricOffer.userId, biometricOffer.displayName);
    } catch (error) {
      if (!isBiometricCancellation(error)) {
        setLoginError("Biometric setup could not be completed. You can enable it later from the menu.");
      }
      finishClientLogin(biometricOffer.token, biometricOffer.userId, biometricOffer.displayName);
    } finally {
      setBiometricLoading(false);
      setBiometricOffer(null);
    }
  };

  // Forgot Password flow
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotStep, setForgotStep] = useState(0);
  const [forgotUserId, setForgotUserId] = useState("");
  const [forgotMethod, setForgotMethod] = useState("BOTH");
  const [forgotRequestId, setForgotRequestId] = useState(null);
  const [forgotDestination, setForgotDestination] = useState("");
  const [forgotOtp, setForgotOtp] = useState("");
  const [forgotResetToken, setForgotResetToken] = useState("");
  const [forgotNewPassword, setForgotNewPassword] = useState("");
  const [forgotConfirmPassword, setForgotConfirmPassword] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState("");
  const [forgotResendSeconds, setForgotResendSeconds] = useState(0);
  const [forgotSuccess, setForgotSuccess] = useState(false);

  const [signupStep, setSignupStep] = useState(0);
  const [pan, setPan] = useState("");
  const [panLoading, setPanLoading] = useState(false);
  const [panWarning, setPanWarning] = useState("");
  const [existingLoginPopup, setExistingLoginPopup] = useState({ open: false, clientName: "" });
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);

  const [customUserId, setCustomUserId] = useState(false);
  const [userId, setUserId] = useState("");
  const [userIdAvailable, setUserIdAvailable] = useState(false);
  const [userIdLoading, setUserIdLoading] = useState(false);

  const [signupPassword, setSignupPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [signupPasswordError, setSignupPasswordError] = useState("");

  const [mobileOtp, setMobileOtp] = useState("");
  const [emailOtp, setEmailOtp] = useState("");
  const [whatsappOtp, setWhatsappOtp] = useState("");
  const [mobileOtpError, setMobileOtpError] = useState("");
  const [emailOtpError, setEmailOtpError] = useState("");
  const [whatsappOtpError, setWhatsappOtpError] = useState("");

  const [mobileOtpSent, setMobileOtpSent] = useState(false);
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [whatsappOtpSent, setWhatsappOtpSent] = useState(false);

  const [mobileVerified, setMobileVerified] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [whatsappVerified, setWhatsappVerified] = useState(false);

  const [whatsappSame, setWhatsappSame] = useState(true);
  const [whatsappNumber, setWhatsappNumber] = useState("");

  const [signupLoading, setSignupLoading] = useState(false);

  const [signupSuccessPopup, setSignupSuccessPopup] = useState(false);

  const postJson = async (url, body) => {
    const appMeta = await getRuntimeAppMetadata();
    const response = await fetch(`${API_BASE}/${url}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...body, ...appMeta }),
    });

    const raw = await response.text();
    try {
      return JSON.parse(raw);
    } catch {
      throw new Error(`Invalid response from ${url}`);
    }
  };

  useEffect(() => {
    if (!forgotOpen || forgotResendSeconds <= 0) return undefined;

    const timer = window.setInterval(() => {
      setForgotResendSeconds((seconds) => Math.max(0, seconds - 1));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [forgotOpen, forgotResendSeconds]);

  useEffect(() => {
    const hasSignupTimer = Object.values(signupOtpCooldowns).some((v) => v > 0);
    if (!hasSignupTimer && otpLoginCooldown <= 0 && forgotUserIdCooldown <= 0) return undefined;
    const timer = window.setInterval(() => {
      setSignupOtpCooldowns((prev) => Object.fromEntries(Object.entries(prev).map(([k,v]) => [k, Math.max(0, v - 1)])));
      setOtpLoginCooldown((v) => Math.max(0, v - 1));
      setForgotUserIdCooldown((v) => Math.max(0, v - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [signupOtpCooldowns, otpLoginCooldown, forgotUserIdCooldown]);

  const resetForgotPassword = () => {
    setForgotStep(0);
    setForgotUserId("");
    setForgotMethod("BOTH");
    setForgotRequestId(null);
    setForgotDestination("");
    setForgotOtp("");
    setForgotResetToken("");
    setForgotNewPassword("");
    setForgotConfirmPassword("");
    setForgotLoading(false);
    setForgotError("");
    setForgotResendSeconds(0);
    setForgotSuccess(false);
  };

  const openForgotPassword = () => {
    resetForgotPassword();
    setForgotOpen(true);
  };

  const closeForgotPassword = () => {
    setForgotOpen(false);
    resetForgotPassword();
  };

  const sendForgotOtp = async ({ resend = false } = {}) => {
    const finalUserId = forgotUserId.trim();

    if (!finalUserId) {
      setForgotError("Please enter your User ID.");
      return;
    }

    if (!["MOBILE", "EMAIL", "WHATSAPP", "BOTH"].includes(forgotMethod)) {
      setForgotError("Please select Mobile, Email, WhatsApp or Both.");
      return;
    }

    if (resend && forgotResendSeconds > 0) return;

    setForgotLoading(true);
    setForgotError("");

    try {
      const data = await postJson("client_forgot_password.php", {
        user_id: finalUserId,
        verification_type: forgotMethod,
      });

      if (data.status !== "ok") {
        if (data.code === "OTP_RESEND_WAIT" && data.retry_after_seconds) {
          setForgotResendSeconds(Number(data.retry_after_seconds) || 30);
        }
        setForgotError(data.message || "Unable to send OTP.");
        return;
      }

      /*
       * For a valid account the backend returns request_id.
       * For an unknown/inactive User ID it intentionally returns a generic
       * accepted response without revealing whether the account exists.
       */
      if (!data.request_id) {
        setForgotError(
          data.message ||
            "If the account details are valid, an OTP will be sent to the registered contact."
        );
        return;
      }

      setForgotRequestId(Number(data.request_id));
      setForgotDestination(data.destination || "");
      setForgotOtp("");
      setForgotResetToken("");
      setForgotResendSeconds(Number(data.resend_after_seconds) || 30);
      setForgotStep(1);

      if (resend) {
        message.success("A new OTP has been sent.");
      } else {
        message.success("OTP sent successfully.");
      }
    } catch (error) {
      setForgotError(error.message || "Unable to send OTP.");
    } finally {
      setForgotLoading(false);
    }
  };

  const verifyForgotOtp = async () => {
    if (!forgotRequestId) {
      setForgotError("Please request a new OTP.");
      return;
    }

    if (!/^\d{6}$/.test(forgotOtp)) {
      setForgotError("Please enter a valid 6-digit OTP.");
      return;
    }

    setForgotLoading(true);
    setForgotError("");

    try {
      const data = await postJson("client_verify_reset_otp.php", {
        request_id: forgotRequestId,
        otp: forgotOtp,
      });

      if (data.status !== "ok" || !data.reset_token) {
        setForgotError(data.message || "OTP verification failed.");
        return;
      }

      // Keep the one-time reset token only in component memory.
      setForgotResetToken(data.reset_token);
      setForgotNewPassword("");
      setForgotConfirmPassword("");
      setForgotStep(2);
      message.success("OTP verified successfully.");
    } catch (error) {
      setForgotError(error.message || "Unable to verify OTP.");
    } finally {
      setForgotLoading(false);
    }
  };

  const completeForgotPassword = async () => {
    if (!forgotRequestId || !forgotResetToken) {
      setForgotError("Password reset authorization has expired. Please start again.");
      return;
    }

    if (forgotNewPassword.length < 8) {
      setForgotError("Password must be at least 8 characters.");
      return;
    }
    if (!/[A-Z]/.test(forgotNewPassword)) {
      setForgotError("Password must contain one uppercase letter.");
      return;
    }
    if (!/[a-z]/.test(forgotNewPassword)) {
      setForgotError("Password must contain one lowercase letter.");
      return;
    }
    if (!/[0-9]/.test(forgotNewPassword)) {
      setForgotError("Password must contain one number.");
      return;
    }
    if (!/[^A-Za-z0-9]/.test(forgotNewPassword)) {
      setForgotError("Password must contain one special character.");
      return;
    }
    if (forgotNewPassword !== forgotConfirmPassword) {
      setForgotError("New Password and Confirm Password do not match.");
      return;
    }

    setForgotLoading(true);
    setForgotError("");

    try {
      const data = await postJson("client_complete_forgot_password.php", {
        request_id: forgotRequestId,
        reset_token: forgotResetToken,
        new_password: forgotNewPassword,
        confirm_password: forgotConfirmPassword,
      });

      if (data.status !== "ok") {
        setForgotError(data.message || "Unable to change password.");
        return;
      }

      // Never retain reset credentials after successful use.
      setForgotResetToken("");
      setForgotOtp("");
      setForgotNewPassword("");
      setForgotConfirmPassword("");
      setForgotSuccess(true);
      setForgotStep(3);
    } catch (error) {
      setForgotError(error.message || "Unable to change password.");
    } finally {
      setForgotLoading(false);
    }
  };

  const completePinLogin = async (forceLogin=false, loginLocation=null) => {
    const saved=await readPinCredential();
    if(!saved?.deviceCredential) throw new Error("PIN login is not configured on this device.");
    const location=loginLocation||await captureRequiredClientLoginLocation();
    const data=await postJson("client_pin_login.php",{device_credential:saved.deviceCredential,pin:pinValue,force_login:forceLogin,location});
    if(data.status==="confirmation_required"||data.code==="ACTIVE_SESSION_EXISTS"){
      setPinOpen(false); setActiveSessionPrompt({loginType:"pin",location,message:data.message||"An active session exists."});return;
    }
    if(data.status!=="ok"||!data.token) throw new Error(data.message||"PIN login failed.");
    localStorage.setItem(LAST_SECURE_METHOD_KEY, "pin");
    finishClientLogin(data.token,data.client?.user_id||saved.userId||"",data.client?.client_name||saved.displayName||"");
  };

  const requestLoginOtp=async()=>{
    if (otpLoginCooldown > 0) return;
    if (!String(otpLoginUserId||"").trim()) { setLoginError("Enter your User ID. If you never created a separate User ID, enter your PAN."); return; }
    setOtpLoginLoading(true);setLoginError("");
    try{const data=await postJson("client_login_otp_request.php",{user_id:otpLoginUserId,method:otpLoginMethod});if(data.status!=="ok"||!data.request_id){if(data.retry_after_seconds)setOtpLoginCooldown(Number(data.retry_after_seconds)||30);throw new Error(data.message||"Unable to send OTP.");}setOtpLoginRequestId(data.request_id);setOtpLoginDestination(data.destination||"");setOtpLoginCooldown(Number(data.resend_after_seconds)||30);saveRememberedUserId(otpLoginUserId);}
    catch(e){setLoginError(e.message||"Unable to send OTP.");}finally{setOtpLoginLoading(false)}
  };
  const completeOtpLogin=async(forceLogin=false,loginLocation=null)=>{
    const location=loginLocation||await captureRequiredClientLoginLocation();
    const data=await postJson("client_login_otp_verify.php",{request_id:otpLoginRequestId,otp:otpLoginValue,force_login:forceLogin,location});
    if(data.status==="confirmation_required"||data.code==="ACTIVE_SESSION_EXISTS"){setActiveSessionPrompt({loginType:"otp",location,message:data.message||"An active session exists."});return;}
    if(data.status!=="ok"||!data.token)throw new Error(data.message||"OTP login failed.");
    setOtpLoginOpen(false);
    if (pinResetRequested) {
      setPinResetRequested(false);
      setPinSetupIsReset(true);
      setPinSetupOffer({token:data.token,userId:data.client?.user_id||otpLoginUserId||"",displayName:data.client?.client_name||""});
      return;
    }
    finishClientLogin(data.token,data.client?.user_id||otpLoginUserId||"",data.client?.client_name||"");
  };
  const enrollPin=async()=>{
    setPinSetupError("");
    if(!/^\d{4}$/.test(pinSetupValue)){setPinSetupError("Please enter a complete 4 digit MPIN.");return;}
    if(!/^\d{4}$/.test(pinSetupConfirm)){setPinSetupError("Please confirm your 4 digit MPIN.");return;}
    if(pinSetupValue !== pinSetupConfirm){setPinSetupError("MPINs do not match. Please enter the same 4 digit MPIN in both fields.");return;}
    setPinLoading(true);try{const r=await fetch(`${API_BASE}/client_pin_enroll.php`,{method:"POST",headers:{"Content-Type":"application/json","X-KG-Client-Authorization":`Bearer ${pinSetupOffer.token}`},body:JSON.stringify({pin:pinSetupValue,device_label:"MFSetu - MyWealth",logout_after_save:pinSetupIsReset})});const d=await r.json().catch(()=>({}));if(!r.ok||d.status!=="ok"||!d.device_credential)throw new Error(d.message||"Unable to save MPIN.");await savePinCredential({deviceCredential:d.device_credential,userId:pinSetupOffer.userId,displayName:pinSetupOffer.displayName});setPinAvailable(true);sessionStorage.removeItem(PIN_SETUP_INTENT_KEY);localStorage.setItem(LAST_SECURE_METHOD_KEY,"pin");setPinSetupValue("");setPinSetupConfirm("");setPinSetupError("");const wasReset=pinSetupIsReset;setPinSetupIsReset(false);setPinSetupOffer(null);if(wasReset){setOtpLoginOpen(false);setOtpLoginRequestId(null);setOtpLoginValue("");setPinResetRequested(false);setLoginError("");setPinValue("");setLoginMode("methods");setShowCredentialLogin(true);setPinOpen(true);return;}finishClientLogin(pinSetupOffer.token,pinSetupOffer.userId,pinSetupOffer.displayName);}catch(e){setPinSetupError(e.message||"Unable to save MPIN. Please try again.");}finally{setPinLoading(false)}
  };

  const completeCredentialLogin = async (values, forceLogin = false, loginLocation = null) => {
    const location = loginLocation || await captureRequiredClientLoginLocation();
    const data = await postJson("client_login.php", {
      user_id: values.user_id,
      password: values.password,
      force_login: forceLogin,
      location,
    });

    if (data.status === "confirmation_required" || data.code === "ACTIVE_SESSION_EXISTS") {
      setActiveSessionPrompt({
        loginType: "password",
        values,
        location,
        message:
          data.message ||
          "Your account is already logged in on another device. Do you want to log out the previous session and continue here?",
      });
      return;
    }

    if (data.status !== "ok" || !data.token) {
      setLoginError(data.message || "Invalid User ID or Password.");
      return;
    }

    const loginUserId = data.client?.user_id || values.user_id || "";
    const loginClientName = data.client?.client_name || "";
    if(values.remember_me) saveRememberedUserId(loginUserId);
    if((pinSetupRequested || sessionStorage.getItem(PIN_SETUP_INTENT_KEY) === "1") && isClientQuickAuthNative()){sessionStorage.removeItem(PIN_SETUP_INTENT_KEY);setPinSetupRequested(false);setPinSetupOffer({token:data.token,userId:loginUserId,displayName:loginClientName});return;}

    if (
      biometricAvailable &&
      (!biometricEnabled || getBiometricAccountHint("client") !== loginUserId)
    ) {
      setBiometricOffer({
        token: data.token,
        userId: loginUserId,
        displayName: loginClientName,
      });
      return;
    }

    finishClientLogin(data.token, loginUserId, loginClientName);
  };

  const forceLoginFromPrompt = async () => {
    const pending = activeSessionPrompt;
    if (!pending) return;

    setActiveSessionPrompt(null);
    setLoginError("");

    if(pending.loginType==="pin"){setPinLoading(true);try{await completePinLogin(true,pending.location)}catch(e){setLoginError(e.message||"PIN login failed.")}finally{setPinLoading(false)}return;}
    if(pending.loginType==="otp"){setOtpLoginLoading(true);try{await completeOtpLogin(true,pending.location)}catch(e){setLoginError(e.message||"OTP login failed.")}finally{setOtpLoginLoading(false)}return;}

    if (pending.loginType === "biometric") {
      setBiometricLoading(true);
      try {
        await completeBiometricLogin(
          pending.saved,
          true,
          pending.location
        );
      } catch (error) {
        setShowCredentialLogin(true);
        setLoginError(
          error.message ||
            "Biometric login could not be completed. Please use PIN, OTP, or User ID and password."
        );
      } finally {
        setBiometricLoading(false);
      }
      return;
    }

    setLoginLoading(true);
    try {
      await completeCredentialLogin(
        pending.values,
        true,
        pending.location
      );
    } catch (error) {
      setLoginError(error.message || "Unable to connect to server.");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleSignIn = async (values) => {
    setLoginLoading(true);
    setLoginError("");

    try {
      await completeCredentialLogin(values, false);
    } catch (error) {
      setLoginError(error.message || "Unable to connect to server.");
    } finally {
      setLoginLoading(false);
    }
  };

  const resetOtpState = () => {
    setMobileOtp("");
    setEmailOtp("");
    setWhatsappOtp("");
    setMobileOtpError("");
    setEmailOtpError("");
    setWhatsappOtpError("");
    setMobileOtpSent(false);
    setEmailOtpSent(false);
    setWhatsappOtpSent(false);
    setMobileVerified(false);
    setEmailVerified(false);
    setWhatsappVerified(false);
  };

  const verifyPan = async () => {
    const normalizedPan = pan.trim().toUpperCase();

    if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(normalizedPan)) {
      message.error("Please enter a valid PAN.");
      return;
    }

    setPanWarning("");
    setPanLoading(true);

    try {
      const data = await postJson("verify_client_pan.php", {
        pan: normalizedPan,
      });

      // PAN exists, but login is already created.
      if (data.code === "LOGIN_ALREADY_EXISTS" || data.status === "already_registered") {
        const existingClient = data.client || {};
        const clientName =
          existingClient.client_name ||
          existingClient.invester_name ||
          existingClient.name ||
          "Client";

        setExistingLoginPopup({ open: true, clientName });
        return;
      }

      // PAN itself is not present in client_master.
      if (data.code === "PAN_NOT_FOUND") {
        setPanWarning("This PAN does not exist in our records. Please contact your distributor.");
        return;
      }

      if (data.status !== "ok") {
        setPanWarning(data.message || "PAN verification failed.");
        return;
      }

      let list = [];
      if (data.client) list = [data.client];
      else if (Array.isArray(data.clients)) list = data.clients;
      else if (Array.isArray(data.distributors)) list = data.distributors;
      else if (Array.isArray(data.arn_options)) list = data.arn_options;

      if (!list.length) {
        message.error(data.message || "No distributor found for this PAN.");
        return;
      }

      setPan(normalizedPan);
      setClients(list);
      setUserId(normalizedPan);
      setCustomUserId(false);
      setUserIdAvailable(false);
      resetOtpState();

      if (list.length === 1) {
        if (list[0].login_exists) {
          Modal.info({
            title: "Account Already Registered",
            content: "A login already exists for this PAN and distributor. Please Sign In.",
            okText: "Go to Sign In",
            onOk: () => setActiveTab("signin"),
          });
          return;
        }

        setSelectedClient(list[0]);
        setSignupStep(2);
      } else {
        setSelectedClient(null);
        setSignupStep(1);
      }
    } catch (error) {
      message.error(error.message || "Unable to verify PAN.");
    } finally {
      setPanLoading(false);
    }
  };

  const handleDistributorSelect = (arnNo) => {
    const found = clients.find((item) => item.arn_no === arnNo);
    if (!found) return;

    if (found.login_exists) {
      message.error("Login is already registered with this distributor.");
      return;
    }

    setSelectedClient(found);
    setSignupStep(2);
    resetOtpState();
  };

  const checkUserId = async (candidate = userId) => {
    const finalUserId = String(candidate || "").trim();

    if (!finalUserId) {
      message.error("Please enter User ID.");
      return false;
    }

    setUserIdLoading(true);

    try {
      const data = await postJson("check_client_userid.php", {
        user_id: finalUserId,
      });

      if (data.available) {
        setUserId(finalUserId);
        setUserIdAvailable(true);
        message.success("User ID is available.");
        return true;
      }

      setUserIdAvailable(false);

      if (Array.isArray(data.suggestions) && data.suggestions.length) {
        message.error(
          `User ID unavailable. Try: ${data.suggestions.join(", ")}`
        );
      } else {
        message.error("This User ID is not available.");
      }

      return false;
    } catch (error) {
      message.error(error.message || "Unable to check User ID.");
      return false;
    } finally {
      setUserIdLoading(false);
    }
  };

  const validatePassword = () => {
    let error = "";

    if (signupPassword.length < 8) {
      error = "Password must be at least 8 characters.";
    } else if (!/[A-Z]/.test(signupPassword)) {
      error = "Password must contain at least one uppercase letter.";
    } else if (!/[a-z]/.test(signupPassword)) {
      error = "Password must contain at least one lowercase letter.";
    } else if (!/[0-9]/.test(signupPassword)) {
      error = "Password must contain at least one number.";
    } else if (!/[^A-Za-z0-9]/.test(signupPassword)) {
      error = "Password must contain at least one special character.";
    } else if (signupPassword !== confirmPassword) {
      error = "Password and Confirm Password do not match.";
    }

    setSignupPasswordError(error);
    return error === "";
  };

  const continueToContactVerification = async () => {
    // Show password-policy / mismatch errors immediately and inline.
    if (!validatePassword()) {
      return;
    }

    if (!selectedClient) {
      message.error("Please select your distributor.");
      return;
    }

    let available = userIdAvailable;

    if (!customUserId && !userIdAvailable) {
      available = await checkUserId(pan.toUpperCase());
      if (!available) {
        setCustomUserId(true);
        setUserId("");
        message.warning("PAN is already used as a User ID. Please choose a different User ID.");
        return;
      }
    }

    if (customUserId && !userIdAvailable) {
      message.error("Please check User ID availability first.");
      return;
    }

    if (!available) return;

    setSignupStep(3);
  };

  const setOtpInlineError = (type, error) => {
    if (type === "MOBILE") setMobileOtpError(error);
    if (type === "EMAIL") setEmailOtpError(error);
    if (type === "WHATSAPP") setWhatsappOtpError(error);
  };

  const getOtpInlineError = (type) => {
    if (type === "MOBILE") return mobileOtpError;
    if (type === "EMAIL") return emailOtpError;
    if (type === "WHATSAPP") return whatsappOtpError;
    return "";
  };

  const sendOtp = async (type) => {
    if ((signupOtpCooldowns[type] || 0) > 0) return;
    if (!selectedClient) {
      message.error("Please select distributor first.");
      return;
    }

    if (type === "WHATSAPP" && !whatsappNumber.trim()) {
      message.error("Please enter WhatsApp number.");
      return;
    }

    try {
      const body = {
        pan: pan.toUpperCase(),
        arn_no: selectedClient.arn_no,
        verification_type: type,
      };

      if (type === "WHATSAPP") {
        body.whatsapp_number = whatsappNumber.trim();
      }

      const data = await postJson("send_client_signup_otp.php", body);

      if (data.status !== "ok") {
        if (data.retry_after_seconds) setSignupOtpCooldowns((prev)=>({...prev,[type]:Number(data.retry_after_seconds)||30}));
        message.error(data.message || "Unable to generate OTP.");
        return;
      }

      if (type === "MOBILE") {
        setMobileOtpSent(true);
        setMobileVerified(false);
      }
      if (type === "EMAIL") {
        setEmailOtpSent(true);
        setEmailVerified(false);
      }
      if (type === "WHATSAPP") {
        setWhatsappOtpSent(true);
        setWhatsappVerified(false);
      }

      setSignupOtpCooldowns((prev) => ({
        ...prev,
        [type]: Number(data.resend_after_seconds) || 30,
      }));
      message.success(`${type === "MOBILE" ? "Mobile" : type === "EMAIL" ? "Email" : "WhatsApp"} OTP sent. You can request another OTP after 30 seconds.`);
    } catch (error) {
      message.error(error.message || "Unable to generate OTP.");
    }
  };

  const verifyOtp = async (type, otp) => {
    setOtpInlineError(type, "");
    if (!/^\d{6}$/.test(otp || "")) {
      setOtpInlineError(type, "Please enter the 6 digit OTP.");
      return;
    }
    try {
      const body = { pan: pan.toUpperCase(), arn_no: selectedClient.arn_no, verification_type: type, otp };
      if (type === "WHATSAPP") body.whatsapp_number = whatsappNumber.trim();
      const data = await postJson("verify_client_signup_otp.php", body);
      if (data.status !== "ok") {
        setOtpInlineError(type, data.message || "Wrong OTP. Please try again.");
        return;
      }
      setOtpInlineError(type, "");
      if (type === "MOBILE") setMobileVerified(true);
      if (type === "EMAIL") setEmailVerified(true);
      if (type === "WHATSAPP") setWhatsappVerified(true);
      message.success(type === "MOBILE" ? "Mobile number verified." : type === "EMAIL" ? "Email ID verified." : "WhatsApp number verified.");
    } catch (error) {
      setOtpInlineError(type, error.message || "Unable to verify OTP.");
    }
  };

  const allRequiredVerified =
    mobileVerified &&
    emailVerified &&
    (whatsappSame || whatsappVerified);

  const createAccount = async () => {
    // Revalidate here too because password fields remain editable after
    // contact verification. Keep the error visible inline in red.
    if (!validatePassword()) {
      return;
    }

    if (!allRequiredVerified) {
      message.error("Please complete all required OTP verifications.");
      return;
    }

    setSignupLoading(true);

    try {
      const data = await postJson("client_signup.php", {
        pan: pan.toUpperCase(),
        arn_no: selectedClient.arn_no,
        user_id: userId.trim(),
        password: signupPassword,
        confirm_password: confirmPassword,
        whatsapp_same_as_mobile: whatsappSame,
        whatsapp_number: whatsappSame ? "" : whatsappNumber.trim(),
      });

      if (data.status !== "ok") {
        message.error(data.message || "Unable to create account.");
        return;
      }
      setSignupSuccessPopup(true);
    } catch (error) {
      message.error(error.message || "Unable to create account.");
    } finally {
      setSignupLoading(false);
    }
  };

  const resetSignup = () => {
    setSignupStep(0);
    setPan("");
    setPanWarning("");
    setClients([]);
    setSelectedClient(null);
    setCustomUserId(false);
    setUserId("");
    setUserIdAvailable(false);
    setSignupPassword("");
    setConfirmPassword("");
    setWhatsappSame(true);
    setWhatsappNumber("");
    resetOtpState();
  };

  const otpRow = ({
    type,
    label,
    destination,
    sent,
    otp,
    setOtp,
    verified,
    icon,
  }) => {
    const otpError = getOtpInlineError(type);
    return (
    <div className="kg-otp-row">
      <div className="kg-contact-cell">
        <div className="kg-field-label">{label}</div>
        <Input
          size="large"
          prefix={icon}
          value={destination || ""}
          readOnly
        />
      </div>

      <Button
        size="large"
        className="kg-generate-btn"
        disabled={verified || (signupOtpCooldowns[type] || 0) > 0}
        onClick={() => sendOtp(type)}
      >
        {(signupOtpCooldowns[type] || 0) > 0
          ? `Wait ${signupOtpCooldowns[type]}s`
          : (sent ? "Regenerate OTP" : "Generate OTP")}
      </Button>

      <Input
        size="large"
        className="kg-otp-input"
        inputMode="numeric"
        maxLength={6}
        placeholder={`Enter ${label} OTP`}
        value={otp}
        status={otpError ? "error" : ""}
        disabled={!sent || verified}
        onChange={(e) => {
          setOtp(e.target.value.replace(/\D/g, "").slice(0, 6));
          if (otpError) setOtpInlineError(type, "");
        }}
      />
      {otpError && <div role="alert" aria-live="assertive" style={{color:"#d92d20",fontSize:12,fontWeight:700,lineHeight:1.3}}>{otpError}</div>}

      <Button
        size="large"
        className="kg-verify-btn"
        disabled={!sent || verified || otp.length !== 6}
        onClick={() => verifyOtp(type, otp)}
      >
        Verify
      </Button>

      <div className={`kg-verified-box ${verified ? "verified" : ""}`}>
        {verified && (
          <>
            <CheckCircleOutlined />
            <span>{label} Verified</span>
          </>
        )}
      </div>
    </div>
    );
  };

  const resetForgotUserId = () => {
    setForgotUserIdPan(""); setForgotUserIdMethod("MOBILE"); setForgotUserIdRequestId(null);
    setForgotUserIdOtp(""); setForgotUserIdDestination(""); setForgotUserIdCooldown(0);
    setForgotUserIdLoading(false); setForgotUserIdError(""); setRecoveredUserIds([]);
  };

  const requestForgotUserIdOtp = async () => {
    if (forgotUserIdCooldown > 0) return;
    const finalPan=forgotUserIdPan.trim().toUpperCase();
    if(!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(finalPan)){setForgotUserIdError("Enter a valid 10-character PAN, for example ABCDE1234F.");return;}
    setForgotUserIdLoading(true); setForgotUserIdError("");
    try{
      const data=await postJson("client_forgot_userid_request.php",{pan:finalPan,method:forgotUserIdMethod});
      if(data.status!=="ok"||!data.request_id){if(data.retry_after_seconds)setForgotUserIdCooldown(Number(data.retry_after_seconds)||30);throw new Error(data.message||"Unable to send OTP.");}
      setForgotUserIdRequestId(data.request_id); setForgotUserIdDestination(data.destination||"");
      setForgotUserIdCooldown(Number(data.resend_after_seconds)||30);
    }catch(e){setForgotUserIdError(e.message||"Unable to send OTP.");}
    finally{setForgotUserIdLoading(false);}
  };

  const verifyForgotUserIdOtp = async () => {
    if(!forgotUserIdRequestId){setForgotUserIdError("Generate an OTP first.");return;}
    if(!/^\d{6}$/.test(forgotUserIdOtp)){setForgotUserIdError("Enter the complete 6-digit OTP.");return;}
    setForgotUserIdLoading(true); setForgotUserIdError("");
    try{
      const data=await postJson("client_forgot_userid_verify.php",{request_id:forgotUserIdRequestId,otp:forgotUserIdOtp});
      if(data.status!=="ok")throw new Error(data.message||"OTP verification failed.");
      setRecoveredUserIds(Array.isArray(data.accounts)?data.accounts:[]);
    }catch(e){setForgotUserIdError(e.message||"OTP verification failed.");}
    finally{setForgotUserIdLoading(false);}
  };

  const openPinChoice = () => {
    setLoginError("");
    if (pinAvailable) {
      setPinOpen(true);
      return;
    }
    sessionStorage.setItem(PIN_SETUP_INTENT_KEY, "1");
    setPinSetupRequested(true);
    setLoginMode("password");
    setLoginError("Sign in once with your User ID and Password to create a secure 4 Digit PIN on this device.");
  };

  const openForgotPin = () => {
    setPinOpen(false);
    setPinValue("");
    setPinResetRequested(true);
    setOtpLoginUserId(getRememberedUserId());
    setOtpLoginRequestId(null);
    setOtpLoginValue("");
    setOtpLoginOpen(true);
  };

  const signInContent = (
    <div className="kg-signin">
      <Title level={4}>Welcome Back</Title>

      {loginError && <Alert className="kg-login-alert" message={loginError} type="error" showIcon />}

      {biometricAvailable && biometricEnabled && !showCredentialLogin ? (
        <div className="mf-biometric-card">
          <div className="mf-biometric-icon"><UnlockOutlined /></div>
          <Title level={4} style={{ marginBottom: 6 }}>Unlock with Biometric</Title>
          <Text type="secondary">Use your fingerprint or face to securely access your account.</Text>
          <Button type="link" block disabled={biometricLoading} onClick={() => { setShowCredentialLogin(true); setLoginMode("methods"); }} style={{ marginTop: 14 }}>
            Cancel / Other Login Options
          </Button>
        </div>
      ) : loginMode === "methods" ? (
        <div className="mf-login-methods">
          <Text type="secondary">Choose a secure login method to continue</Text>

          {biometricAvailable && biometricEnabled && (
            <Button className="mf-login-method" size="large" block icon={<UnlockOutlined />} loading={biometricLoading} onClick={() => handleBiometricLogin(false)}>
              <span><strong>Login with Biometric</strong><small>Use fingerprint / face</small></span>
            </Button>
          )}

          {isClientQuickAuthNative() && (
            <Button className="mf-login-method" size="large" block icon={<KeyOutlined />} onClick={openPinChoice}>
              <span><strong>{pinAvailable ? "Login with 4 Digit PIN" : "Set Up 4 Digit PIN"}</strong><small>{pinAvailable ? "Quick and secure on this device" : "Create PIN after one secure sign-in"}</small></span>
            </Button>
          )}

          <Button className="mf-login-method" size="large" block icon={<MobileOutlined />} onClick={() => { setPinResetRequested(false); setOtpLoginOpen(true); setOtpLoginUserId(getRememberedUserId()); }}>
            <span><strong>Login with OTP</strong><small>OTP is sent only when you request it</small></span>
          </Button>

          <Button className="mf-login-method" size="large" block icon={<UserOutlined />} onClick={() => setLoginMode("password")}>
            <span><strong>Login with User ID & Password</strong><small>Use your credentials</small></span>
          </Button>
        </div>
      ) : (
        <>
          <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => { setLoginMode("methods"); sessionStorage.removeItem(PIN_SETUP_INTENT_KEY); setPinSetupRequested(false); setLoginError(""); }} style={{ paddingLeft: 0 }}>
            Other Login Options
          </Button>
          <Text type="secondary">Use your User ID (or PAN if you never created a separate User ID) and password.</Text>
          <Form className="kg-signin-form" layout="vertical" onFinish={handleSignIn} autoComplete="off" initialValues={{ user_id: getRememberedUserId(), remember_me: !!getRememberedUserId() }}>
            <Form.Item label="User ID" name="user_id" normalize={(value) => String(value || "").toUpperCase()} rules={[{ required: true, message: "Please enter your User ID" }]}>
              <Input size="large" prefix={<UserOutlined />} placeholder="Enter User ID or PAN" />
            </Form.Item>
            <div className="kg-field-help">If you have not created a separate User ID, enter your PAN here.</div>
            <Form.Item label="Password" name="password" rules={[{ required: true, message: "Please enter your Password" }]}>
              <Input.Password size="large" prefix={<LockOutlined />} placeholder="Enter Password" />
            </Form.Item>
            <Form.Item name="remember_me" valuePropName="checked" style={{marginBottom:10}}><Checkbox>Remember my User ID</Checkbox></Form.Item>
            <Button type="primary" htmlType="submit" size="large" block loading={loginLoading}>{pinSetupRequested ? "Sign In & Set Up PIN" : "Sign In"}</Button>
            <div className="kg-forgot-links">
              <Button type="link" onClick={()=>{setForgotUserIdOpen(true);setForgotUserIdError("");setRecoveredUserIds([]);}}>Forgot User ID?</Button>
              <span>|</span>
              <Button type="link" onClick={openForgotPassword}>Forgot Password?</Button>
            </div>
          </Form>
        </>
      )}
    </div>
  );

  const signUpContent = (
    <div className="kg-signup-layout">
      <aside className="kg-step-sidebar">
        <Steps
          direction="vertical"
          current={signupStep}
          items={[
            { title: "Verify PAN" },
            { title: "Select Distributor" },
            { title: "Create User ID & Password" },
            { title: "Verify Contact Details" },
          ]}
        />

        <div className="kg-security-card">
          <SafetyOutlined />
          <div>
            <strong>Secure & Safe</strong>
            <span>Your information is protected.</span>
          </div>
        </div>
      </aside>

      <main className="kg-signup-main">
        <section className="kg-form-section">
          <div className="kg-section-heading">1. Verify PAN</div><div className="kg-step-help">Enter the PAN registered in your mutual-fund investment records. PAN must be 10 characters, for example ABCDE1234F.</div>

          <div className="kg-pan-row">
            <Input
              size="large"
              prefix={<IdcardOutlined />}
              placeholder="Enter PAN Number"
              value={pan}
              maxLength={10}
              disabled={signupStep > 0}
              onChange={(e) =>
                setPan(
                  e.target.value
                    .toUpperCase()
                    .replace(/[^A-Z0-9]/g, "")
                )
              }
              onPressEnter={verifyPan}
            />

            <Button
              type="primary"
              size="large"
              loading={panLoading}
              disabled={signupStep > 0}
              onClick={verifyPan}
            >
              Verify PAN
            </Button>
          </div>

          {panWarning && (
            <Alert
              type="error"
              showIcon
              message={panWarning}
              style={{ marginTop: 12 }}
            />
          )}
        </section>

        {signupStep >= 1 && (
          <section className="kg-form-section">
            <div className="kg-section-heading">2. Select Distributor</div><div className="kg-step-help">Choose the distributor/ARN under which you want to access your portfolio. Already registered profiles are clearly marked.</div>

            {clients.length > 1 ? (
              <Select
                size="large"
                className="kg-full-width"
                placeholder="Select Distributor (ARN)"
                value={selectedClient?.arn_no}
                onChange={handleDistributorSelect}
                options={clients.map((client) => ({
                  value: client.arn_no,
                  disabled: !!client.login_exists,
                  label:
                    `${client.distributor_name || client.arn_holder_name || "Distributor"} (${client.arn_no})` +
                    (client.login_exists ? " - Already Registered" : ""),
                }))}
              />
            ) : selectedClient ? (
              <div className="kg-selected-client">
                <strong>{selectedClient.client_name}</strong>
                <span>
                  {selectedClient.distributor_name ||
                    selectedClient.arn_holder_name ||
                    "Distributor"}{" "}
                  • {selectedClient.arn_no}
                </span>
              </div>
            ) : null}
          </section>
        )}

        {signupStep >= 2 && selectedClient && (
          <section className="kg-form-section">
            <div className="kg-section-heading">
              3. Create User ID & Password
            </div>
            <div className="kg-step-help">By default your PAN is used as your User ID. Tick “Create a different User ID” only if you want a separate login name. Keep your password private and do not share OTPs with anyone.</div>

            <div className="kg-credential-grid">
              <div>
                <div className="kg-field-label">User ID</div><div className="kg-field-help">Your PAN is the default User ID. You may create a separate User ID below.</div>
                <Input
                  size="large"
                  value={userId}
                  disabled={!customUserId}
                  onChange={(e) => {
                    setUserId(e.target.value);
                    setUserIdAvailable(false);
                  }}
                />

                <Checkbox
                  className="kg-custom-user-checkbox"
                  checked={customUserId}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setCustomUserId(checked);
                    setUserId(checked ? "" : pan.toUpperCase());
                    setUserIdAvailable(false);
                  }}
                >
                  Create a different User ID
                </Checkbox>

                {customUserId && (
                  <div className="kg-userid-actions">
                    <Button
                      loading={userIdLoading}
                      onClick={() => checkUserId()}
                    >
                      Check Availability
                    </Button>

                    {userIdAvailable && (
                      <span className="kg-available">
                        <CheckCircleOutlined /> Available
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div>
                <div className="kg-field-label">Password</div>

                <Input.Password
                  size="large"
                  prefix={<LockOutlined />}
                  placeholder="Create Password"
                  value={signupPassword}
                  status={signupPasswordError ? "error" : ""}
                  onChange={(e) => {
                    setSignupPassword(e.target.value);
                    if (signupPasswordError) setSignupPasswordError("");
                  }}
                />

                <Input.Password
                  className="kg-confirm-password"
                  size="large"
                  prefix={<LockOutlined />}
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  status={signupPasswordError ? "error" : ""}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (signupPasswordError) setSignupPasswordError("");
                  }}
                />

                {signupPasswordError && (
                  <div
                    role="alert"
                    aria-live="assertive"
                    style={{
                      marginTop: 8,
                      color: "#d92d20",
                      fontSize: 13,
                      fontWeight: 700,
                      lineHeight: 1.45,
                    }}
                  >
                    {signupPasswordError}
                  </div>
                )}
              </div>
            </div>

            {signupStep === 2 && (
              <Button
                type="primary"
                size="large"
                loading={userIdLoading}
                onClick={continueToContactVerification}
              >
                Continue to Contact Verification
              </Button>
            )}
          </section>
        )}

        {signupStep >= 3 && selectedClient && (
          <section className="kg-form-section">
            <div className="kg-section-heading">
              4. Verify Contact Details
            </div>

            {otpRow({
              type: "MOBILE",
              label: "Mobile Number",
              destination: selectedClient.masked_mobile,
              sent: mobileOtpSent,
              otp: mobileOtp,
              setOtp: setMobileOtp,
              verified: mobileVerified,
              icon: <PhoneOutlined />,
            })}

            {otpRow({
              type: "EMAIL",
              label: "Email ID",
              destination: selectedClient.masked_email,
              sent: emailOtpSent,
              otp: emailOtp,
              setOtp: setEmailOtp,
              verified: emailVerified,
              icon: <MailOutlined />,
            })}

            <div className="kg-whatsapp-choice">
              <Checkbox
                checked={whatsappSame}
                onChange={(e) => {
                  setWhatsappSame(e.target.checked);
                  setWhatsappNumber("");
                  setWhatsappOtp("");
                  setWhatsappOtpSent(false);
                  setWhatsappVerified(false);
                }}
              >
                WhatsApp number is same as registered mobile
              </Checkbox>
            </div>

            {!whatsappSame && (
              <>
                <div className="kg-whatsapp-number">
                  <div className="kg-field-label">WhatsApp Number</div>
                  <Input
                    size="large"
                    prefix={<WhatsAppOutlined />}
                    placeholder="Enter WhatsApp Number"
                    value={whatsappNumber}
                    maxLength={15}
                    onChange={(e) => {
                      setWhatsappNumber(
                        e.target.value.replace(/[^\d+]/g, "")
                      );
                      setWhatsappOtpSent(false);
                      setWhatsappVerified(false);
                    }}
                  />
                </div>

                {otpRow({
                  type: "WHATSAPP",
                  label: "WhatsApp Number",
                  destination: whatsappNumber,
                  sent: whatsappOtpSent,
                  otp: whatsappOtp,
                  setOtp: setWhatsappOtp,
                  verified: whatsappVerified,
                  icon: <WhatsAppOutlined />,
                })}
              </>
            )}

            <Alert
              className="kg-verification-alert"
              type={allRequiredVerified ? "success" : "info"}
              showIcon
              message={
                allRequiredVerified
                  ? "All required verifications completed. You can now create your account."
                  : "Mobile and Email verification are mandatory. Verify WhatsApp also if it is different from Mobile."
              }
            />

            <Button
              type="primary"
              size="large"
              block
              loading={signupLoading}
              disabled={!allRequiredVerified}
              onClick={createAccount}
            >
              Create Account
            </Button>
          </section>
        )}

        {signupStep > 0 && (
          <Button type="link" onClick={resetSignup}>
            Start Again / Change PAN
          </Button>
        )}
      </main>
    </div>
  );

  const content = (
    <div className="kg-client-login">
      {!embedded && (
        <header className="mf-brand-hero">
          <img className="mf-brand-logo" src={mfsetuLogo} alt="MFSetu MyWealth" />
          <div className="mf-brand-tagline">MY GOALS. MY WEALTH. FOR LIFE.</div>
        </header>
      )}

      <div className="mf-auth-shell">
        <div className="mf-auth-choice" role="group" aria-label="Account access">
          <Button
            className={activeTab === "signin" ? "mf-choice-btn active" : "mf-choice-btn"}
            type={activeTab === "signin" ? "primary" : "default"}
            icon={<UserOutlined />}
            onClick={() => setActiveTab("signin")}
          >
            <span>Sign In<small>Existing Client</small></span>
          </Button>
          <Button
            className={activeTab === "signup" ? "mf-choice-btn signup active" : "mf-choice-btn signup"}
            type={activeTab === "signup" ? "primary" : "default"}
            onClick={() => setActiveTab("signup")}
          >
            <span>Sign Up<small>New User?</small></span>
          </Button>
        </div>

        <div className="mf-auth-content">
          {activeTab === "signin" ? signInContent : signUpContent}
        </div>

        {!embedded && (
          <div className="mf-trust-strip">
            <span><SafetyOutlined /> Secure Access</span>
            <span>Investments</span>
            <span>Family Portfolio</span>
            <span>Support</span>
          </div>
        )}
      </div>

      <Modal
        open={!!activeSessionPrompt}
        centered
        title="Already Logged In"
        okText="Logout Previous & Continue"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
        confirmLoading={loginLoading || biometricLoading || pinLoading || otpLoginLoading}
        closable={!(loginLoading || biometricLoading || pinLoading || otpLoginLoading)}
        maskClosable={false}
        keyboard={!(loginLoading || biometricLoading || pinLoading || otpLoginLoading)}
        onCancel={() => {
          if (!(loginLoading || biometricLoading || pinLoading || otpLoginLoading)) { const wasPin = activeSessionPrompt?.loginType === "pin"; setActiveSessionPrompt(null); if (wasPin) setPinOpen(true); }
        }}
        onOk={forceLoginFromPrompt}
      >
        <Alert
          type="warning"
          showIcon
          message="Active session detected"
          description={
            activeSessionPrompt?.message ||
            "Your account is already logged in on another device."
          }
        />
      </Modal>

      <Modal
        open={!!biometricOffer}
        centered
        closable={false}
        maskClosable={false}
        keyboard={false}
        title="Enable Biometric Login?"
        footer={[
          <Button key="later" disabled={biometricLoading} onClick={() => {
            const offer = biometricOffer;
            setBiometricOffer(null);
            if (offer) finishClientLogin(offer.token, offer.userId, offer.displayName);
          }}>
            Not Now
          </Button>,
          <Button key="enable" type="primary" loading={biometricLoading} onClick={acceptBiometricOffer}>
            Enable
          </Button>,
        ]}
      >
        <Text>Use your device biometrics for faster secure sign-in on this mobile device. Your login password will not be stored.</Text>
      </Modal>

      <Modal
        open={forgotOpen}
        centered
        width={520}
        closable={!forgotLoading}
        maskClosable={false}
        keyboard={!forgotLoading}
        destroyOnHidden
        title="Forgot Password"
        footer={null}
        onCancel={closeForgotPassword}
      >
        <div style={{ paddingTop: 4 }}>
          <Steps
            size="small"
            current={Math.min(forgotStep, 2)}
            items={[
              { title: "Verify Account" },
              { title: "Verify OTP" },
              { title: "New Password" },
            ]}
            style={{ marginBottom: 24 }}
          />

          {forgotError && (
            <Alert
              type="error"
              showIcon
              message={forgotError}
              style={{ marginBottom: 16 }}
            />
          )}

          {forgotStep === 0 && (
            <>
              <Text type="secondary">
                Enter your User ID and choose where you want to receive the
                password reset OTP.
              </Text>

              <div style={{ marginTop: 18 }}>
                <div className="kg-field-label">User ID</div>
                <Input
                  size="large"
                  prefix={<UserOutlined />}
                  placeholder="Enter User ID"
                  value={forgotUserId}
                  maxLength={50}
                  onChange={(e) => {
                    setForgotUserId(e.target.value.toUpperCase());
                    setForgotError("");
                  }}
                  onPressEnter={() => sendForgotOtp()}
                />
              </div>

              <div style={{ marginTop: 16 }}>
                <div className="kg-field-label">Send OTP To</div>
                <Select
                  size="large"
                  style={{ width: "100%" }}
                  value={forgotMethod}
                  onChange={(value) => {
                    setForgotMethod(value);
                    setForgotError("");
                  }}
                  options={[
                    {
                      value: "BOTH",
                      label: "Both - Registered Mobile & Email",
                    },
                    {
                      value: "MOBILE",
                      label: "Registered Mobile Only",
                    },
                    {
                      value: "EMAIL",
                      label: "Registered Email Only",
                    },
                    {
                      value: "WHATSAPP",
                      label: "Registered WhatsApp Only",
                    },
                  ]}
                />
              </div>

              <Button
                type="primary"
                size="large"
                block
                loading={forgotLoading}
                onClick={() => sendForgotOtp()}
                style={{ marginTop: 22 }}
              >
                Send OTP
              </Button>
            </>
          )}

          {forgotStep === 1 && (
            <>
              <Text type="secondary">
                Enter the 6-digit OTP sent to your registered contact
                {forgotDestination ? ` (${forgotDestination})` : ""}.
              </Text>

              <Input
                size="large"
                prefix={<SafetyOutlined />}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="Enter 6-digit OTP"
                value={forgotOtp}
                onChange={(e) => {
                  setForgotOtp(
                    e.target.value.replace(/\D/g, "").slice(0, 6)
                  );
                  setForgotError("");
                }}
                onPressEnter={verifyForgotOtp}
                style={{ marginTop: 18 }}
              />

              <Button
                type="primary"
                size="large"
                block
                loading={forgotLoading}
                disabled={forgotOtp.length !== 6}
                onClick={verifyForgotOtp}
                style={{ marginTop: 16 }}
              >
                Verify OTP
              </Button>

              <div
                style={{
                  marginTop: 12,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <Button
                  type="link"
                  style={{ paddingLeft: 0 }}
                  disabled={forgotLoading || forgotResendSeconds > 0}
                  onClick={() => sendForgotOtp({ resend: true })}
                >
                  {forgotResendSeconds > 0
                    ? `Resend OTP in ${forgotResendSeconds}s`
                    : "Resend OTP"}
                </Button>

                <Button
                  type="link"
                  disabled={forgotLoading}
                  onClick={() => {
                    setForgotStep(0);
                    setForgotRequestId(null);
                    setForgotDestination("");
                    setForgotOtp("");
                    setForgotResetToken("");
                    setForgotResendSeconds(0);
                    setForgotError("");
                  }}
                >
                  Change Method
                </Button>
              </div>
            </>
          )}

          {forgotStep === 2 && (
            <>
              <Alert
                type="success"
                showIcon
                message="OTP verified. Create your new password."
                style={{ marginBottom: 16 }}
              />

              <div className="kg-field-label">New Password</div>
              <Input.Password
                size="large"
                prefix={<LockOutlined />}
                placeholder="Enter New Password"
                value={forgotNewPassword}
                autoComplete="new-password"
                onChange={(e) => {
                  setForgotNewPassword(e.target.value);
                  setForgotError("");
                }}
              />

              <div className="kg-field-label" style={{ marginTop: 14 }}>
                Confirm New Password
              </div>
              <Input.Password
                size="large"
                prefix={<LockOutlined />}
                placeholder="Confirm New Password"
                value={forgotConfirmPassword}
                autoComplete="new-password"
                onChange={(e) => {
                  setForgotConfirmPassword(e.target.value);
                  setForgotError("");
                }}
                onPressEnter={completeForgotPassword}
              />

              <Text
                type="secondary"
                style={{ display: "block", marginTop: 10, fontSize: 12 }}
              >
                Minimum 8 characters with uppercase, lowercase, number and
                special character.
              </Text>

              <Button
                type="primary"
                size="large"
                block
                loading={forgotLoading}
                onClick={completeForgotPassword}
                style={{ marginTop: 18 }}
              >
                Change Password
              </Button>
            </>
          )}

          {forgotStep === 3 && forgotSuccess && (
            <div style={{ textAlign: "center", padding: "8px 0" }}>
              <CheckCircleOutlined
                style={{ fontSize: 48, marginBottom: 14 }}
              />
              <Title level={4}>Password Changed Successfully</Title>
              <Text type="secondary">
                Your password has been changed. Please sign in using your new
                password.
              </Text>

              <Button
                type="primary"
                size="large"
                block
                style={{ marginTop: 22 }}
                onClick={() => {
                  closeForgotPassword();
                  setLoginError("");
                  setActiveTab("signin");
                }}
              >
                Go to Sign In
              </Button>
            </div>
          )}
        </div>
      </Modal>

      <Modal
        open={signupSuccessPopup}
        centered
        closable={false}
        maskClosable={false}
        keyboard={false}
        title="Account Created Successfully"
        okText="Sign In"
        cancelButtonProps={{ style: { display: "none" } }}
        onOk={() => {
          setSignupSuccessPopup(false);
          resetSignup();
          setLoginError("");
          setActiveTab("signin");
        }}
      >
        <div style={{ fontSize: 16, lineHeight: 1.7 }}>
          Your MFSetu MyWealth account has been created successfully.
          Please Sign In using your User ID and Password.
        </div>
      </Modal>

      <Modal
        open={existingLoginPopup.open}
        centered
        closable={false}
        maskClosable={false}
        keyboard={false}
        title="Account Already Registered"
        okText="Go to Sign In"
        cancelButtonProps={{ style: { display: "none" } }}
        onOk={() => {
          setExistingLoginPopup({ open: false, clientName: "" });
          resetSignup();
          setLoginError("");
          setActiveTab("signin");
        }}
      >
        <div style={{ fontSize: 16, lineHeight: 1.7 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>
            Hey!!! {existingLoginPopup.clientName || "Client"}
          </div>
          <div>
            Your Login Profile is already created. Please Sign In using your existing User ID and Password.
          </div>
        </div>
      </Modal>

    </div>
  );

  const quickAuthModals = <>
    {pinOpen && <div className="mf-mpin-screen">
      <button className="mf-mpin-back" onClick={()=>{setPinOpen(false);setPinValue("");setLoginError("");setLoginMode("methods")}} aria-label="Back">←</button>
      <div className="mf-mpin-card">
        <img className="mf-mpin-logo" src={mfsetuLogo} alt="MFSetu MyWealth" />
        <div className="mf-mpin-lock"><LockOutlined /></div>
        <Title level={3}>Enter Your 4 Digit MPIN</Title>
        <Text type="secondary">Enter the MPIN you created on this device</Text>
        {loginError && <Alert className="mf-mpin-error" type="error" showIcon message={loginError} />}
        <MpinDigits value={pinValue} onChange={(v)=>{setPinValue(v);setLoginError("")}} disabled={pinLoading} ariaLabel="Login MPIN" groupId="login-mpin" autoFocus />
        <Button className="mf-mpin-primary" type="primary" block size="large" loading={pinLoading} disabled={pinValue.length!==4} onClick={async()=>{setPinLoading(true);setLoginError("");try{await completePinLogin(false)}catch(e){setLoginError(e.message||"Unable to login with MPIN.")}finally{setPinLoading(false)}}}>Login</Button>
        <Button type="link" className="mf-mpin-link" onClick={openForgotPin}>Forgot MPIN?</Button>
        <Button type="link" className="mf-mpin-link mf-muted-link" onClick={()=>{setPinOpen(false);setPinValue("");setLoginError("");setLoginMode("methods")}}>Other Login Options</Button>
      </div>
    </div>}
    {!!pinSetupOffer && <div className="mf-mpin-screen">
      <div className="mf-mpin-card">
        <img className="mf-mpin-logo" src={mfsetuLogo} alt="MFSetu MyWealth" />
        <div className="mf-mpin-lock"><KeyOutlined /></div>
        <Title level={3}>{pinSetupIsReset ? "Create New MPIN" : "Set Up Your 4 Digit MPIN"}</Title>
        <Text type="secondary">{pinSetupIsReset ? "Choose a new MPIN. Your previous MPIN cannot be reused." : "Create a secure 4 digit MPIN for quick login on this device."}</Text>
        {pinSetupError && <Alert className="mf-mpin-error" type="error" showIcon message={pinSetupError} />}
        <div className="mf-mpin-field-label">New MPIN</div>
        <MpinDigits value={pinSetupValue} onChange={(v)=>{setPinSetupValue(v);setPinSetupError("")}} disabled={pinLoading} ariaLabel="New MPIN" groupId="new-mpin" autoFocus onComplete={()=>document.getElementById("confirm-mpin-0")?.focus()} />
        <div className="mf-mpin-field-label">Confirm New MPIN</div>
        <MpinDigits value={pinSetupConfirm} onChange={(v)=>{setPinSetupConfirm(v); if(v.length===4 && pinSetupValue.length===4 && v!==pinSetupValue) setPinSetupError("MPINs do not match. Please enter the same 4 digit MPIN in both fields."); else setPinSetupError("")}} disabled={pinLoading} ariaLabel="Confirm MPIN" groupId="confirm-mpin" />
        <Button className="mf-mpin-primary" type="primary" block size="large" loading={pinLoading} disabled={pinSetupValue.length!==4||pinSetupConfirm.length!==4} onClick={enrollPin}>Save MPIN</Button>
        {!pinSetupIsReset && <Button type="link" className="mf-mpin-link mf-muted-link" onClick={()=>{const o=pinSetupOffer;setPinSetupOffer(null);setPinSetupValue("");setPinSetupConfirm("");setPinSetupError("");if(o)finishClientLogin(o.token,o.userId,o.displayName)}}>Skip for now</Button>}
      </div>
    </div>}
    <Modal className={pinResetRequested ? "mf-forgot-mpin-modal" : ""} title={pinResetRequested ? "Reset MPIN with OTP" : "Login with OTP"} open={otpLoginOpen} onCancel={()=>{setOtpLoginOpen(false);setOtpLoginRequestId(null);setOtpLoginValue("");setOtpLoginCooldown(0)}} footer={null}>
      {pinResetRequested && <div className="mf-forgot-mpin-hero"><img src={mfsetuLogo} alt="MFSetu MyWealth" /><div className="mf-mpin-lock"><KeyOutlined /></div><Title level={3}>Forgot MPIN?</Title><Text type="secondary">Verify your identity with OTP and create a new 4 digit MPIN.</Text></div>}
      {loginError && <Alert type="error" showIcon message={loginError} style={{marginBottom:12}} />}
      <div className="kg-modal-help">Enter your User ID. If you never created a separate User ID, enter your PAN. We will send a 6-digit OTP only to your registered contact.{pinResetRequested && " After verification, you can create a new MPIN."}</div>
      <Input value={otpLoginUserId} onChange={e=>setOtpLoginUserId(e.target.value.toUpperCase())} placeholder="User ID or PAN" style={{marginBottom:10}}/>
      <Select value={otpLoginMethod} onChange={(v)=>{setOtpLoginMethod(v);setLoginError("");setOtpLoginRequestId(null);setOtpLoginValue("");}} style={{width:"100%",marginBottom:10}} options={[{value:"MOBILE",label:"Registered Mobile"},{value:"EMAIL",label:"Registered Email"},{value:"WHATSAPP",label:"Registered WhatsApp"}]}/>
      {!otpLoginRequestId
        ? <Button type="primary" block loading={otpLoginLoading} disabled={otpLoginCooldown>0} onClick={requestLoginOtp}>{otpLoginCooldown>0?`Generate OTP again in ${otpLoginCooldown}s`:"Generate OTP"}</Button>
        : <>
            <Alert type="success" showIcon message={`OTP sent to ${otpLoginDestination || "your registered contact"}. It is valid for 10 minutes.`} />
            <Input inputMode="numeric" maxLength={6} value={otpLoginValue} onChange={e=>setOtpLoginValue(e.target.value.replace(/\D/g,"").slice(0,6))} placeholder="Enter 6 digit OTP" style={{margin:"12px 0 10px"}}/>
            <Button type="primary" block loading={otpLoginLoading} disabled={otpLoginValue.length!==6} onClick={async()=>{setOtpLoginLoading(true);try{await completeOtpLogin(false)}catch(e){setLoginError(e.message||"OTP login failed.")}finally{setOtpLoginLoading(false)}}}>{pinResetRequested ? "Verify OTP & Continue" : "Verify OTP & Login"}</Button>
            <Button type="link" block disabled={otpLoginLoading||otpLoginCooldown>0} onClick={requestLoginOtp}>{otpLoginCooldown>0?`Regenerate OTP in ${otpLoginCooldown}s`:"Regenerate OTP"}</Button>
          </>}
    </Modal>
    <Modal title="Recover User ID" open={forgotUserIdOpen} onCancel={()=>{setForgotUserIdOpen(false);resetForgotUserId();}} footer={null}>
      <div className="kg-modal-help">Enter the PAN linked to your MFSetu MyWealth account. After OTP verification we will show the User ID(s) registered against that PAN.</div>
      {forgotUserIdError && <Alert type="error" showIcon message={forgotUserIdError} style={{marginBottom:12}}/>}
      {recoveredUserIds.length===0 ? <>
        <Input value={forgotUserIdPan} maxLength={10} onChange={e=>setForgotUserIdPan(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g,""))} placeholder="PAN e.g. ABCDE1234F" style={{marginBottom:10}} disabled={!!forgotUserIdRequestId}/>
        <Select value={forgotUserIdMethod} onChange={setForgotUserIdMethod} disabled={!!forgotUserIdRequestId} style={{width:"100%",marginBottom:10}} options={[{value:"MOBILE",label:"Send OTP to Registered Mobile"},{value:"EMAIL",label:"Send OTP to Registered Email"},{value:"WHATSAPP",label:"Send OTP to Registered WhatsApp"}]}/>
        {!forgotUserIdRequestId ? <Button type="primary" block loading={forgotUserIdLoading} disabled={forgotUserIdCooldown>0} onClick={requestForgotUserIdOtp}>{forgotUserIdCooldown>0?`Generate OTP again in ${forgotUserIdCooldown}s`:"Generate OTP"}</Button> : <>
          <Alert type="success" showIcon message={`OTP sent to ${forgotUserIdDestination || "your registered contact"}.`} />
          <Input inputMode="numeric" maxLength={6} value={forgotUserIdOtp} onChange={e=>setForgotUserIdOtp(e.target.value.replace(/\D/g,"").slice(0,6))} placeholder="Enter 6 digit OTP" style={{margin:"12px 0 10px"}}/>
          <Button type="primary" block loading={forgotUserIdLoading} disabled={forgotUserIdOtp.length!==6} onClick={verifyForgotUserIdOtp}>Verify OTP & Show User ID</Button>
          <Button type="link" block disabled={forgotUserIdLoading||forgotUserIdCooldown>0} onClick={requestForgotUserIdOtp}>{forgotUserIdCooldown>0?`Regenerate OTP in ${forgotUserIdCooldown}s`:"Regenerate OTP"}</Button>
        </>}
      </> : <div className="kg-recovered-userids"><Alert type="success" showIcon message="User ID recovered successfully."/><div className="kg-recovery-list">{recoveredUserIds.map((a,i)=><div className="kg-recovery-item" key={`${a.user_id}-${i}`}><strong>{a.user_id}</strong><span>{a.arn_no ? `Distributor: ${a.arn_no}` : ""}</span></div>)}</div><Button type="primary" block onClick={()=>{setForgotUserIdOpen(false);resetForgotUserId();}}>Back to Sign In</Button></div>}
    </Modal>
  </>;

  if (embedded) return <>{content}{quickAuthModals}</>;
  return (
    <div className="kg-client-standalone">
      {content}
      {quickAuthModals}
    </div>
  );
}

export default ClientLogin;
